import React, { useMemo } from 'react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { getChartThemeColors } from '../../utils/chartColors';

/**
 * Compact, mobile-first Elo trend for one session, on the Session Summary tab.
 * Reuses the same theme colors as EloHistoryChart on the full performance tab.
 *
 * Two modes:
 *  - Single ladder ('mixed' | 'levels'): pass `series`.
 *  - 'all' ladder view: pass `dualSeries={{ mixed, levels }}` — renders both
 *    lines (mirrors EloHistoryChart's showMixed/showLevels pattern) when both
 *    ladders were played, or falls back to a single line automatically when
 *    only one was.
 *
 * @param {Array<{ gameNumber: number, newElo: number, delta: number, result: string }>} [series]
 * @param {{ mixed: Array, levels: Array }} [dualSeries]
 * @param {'all'|'mixed'|'levels'} [ladderView='mixed']
 */
function EloTrendTooltip({ active, payload, dual, colors }) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;

  if (!dual) {
    return (
      <div className="min-w-[7rem] rounded-xl border border-border-light bg-surface p-2.5 shadow-brand-md">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
          Game {point.gameNumber}
        </p>
        <p className="mt-1 text-sm font-bold tabular-nums text-text-primary">{Math.round(point.newElo)}</p>
        {point.delta != null && point.delta !== 0 && (
          <p
            className={`text-xs font-semibold tabular-nums ${
              point.delta > 0 ? 'text-success-dark' : 'text-danger-dark'
            }`}
          >
            {point.delta > 0 ? '+' : ''}
            {point.delta}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="min-w-[8rem] rounded-xl border border-border-light bg-surface p-2.5 shadow-brand-md">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
        Game {point.gameNumber}
      </p>
      <div className="mt-1.5 space-y-1">
        {point.mixedElo != null && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-medium" style={{ color: colors.mixed }}>Mixed</span>
            <span className="text-sm font-bold tabular-nums text-text-primary">{Math.round(point.mixedElo)}</span>
          </div>
        )}
        {point.levelsElo != null && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-medium" style={{ color: colors.levels }}>Levels</span>
            <span className="text-sm font-bold tabular-nums text-text-primary">{Math.round(point.levelsElo)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ResultDot({ cx, cy, payload, colors, dataKey }) {
  if (cx == null || cy == null) return null;
  const result =
    dataKey === 'levelsElo' ? payload.levelsResult : dataKey === 'mixedElo' ? payload.mixedResult : payload.result;
  const fill = result === 'win' ? colors.win : result === 'loss' ? colors.loss : colors.textMuted;
  return <circle cx={cx} cy={cy} r={3.5} fill={fill} stroke={colors.surface} strokeWidth={1.5} />;
}

export default function SessionEloTrendChart({ series, dualSeries, ladderView = 'mixed' }) {
  const theme = getChartThemeColors();

  const mixedSeries = dualSeries?.mixed || [];
  const levelsSeries = dualSeries?.levels || [];
  const isDual = ladderView === 'all' && mixedSeries.length > 0 && levelsSeries.length > 0;

  const fallbackSingle =
    ladderView === 'all' && !isDual
      ? mixedSeries.length
        ? { data: mixedSeries, color: theme.mixed }
        : { data: levelsSeries, color: theme.levels }
      : null;

  const effectiveSeries = fallbackSingle ? fallbackSingle.data : series;
  const lineColor = fallbackSingle ? fallbackSingle.color : ladderView === 'levels' ? theme.levels : theme.mixed;

  const combinedData = useMemo(() => {
    if (!isDual) return null;
    const maxLen = Math.max(mixedSeries.length, levelsSeries.length);
    const points = [];
    for (let i = 0; i < maxLen; i += 1) {
      const m = mixedSeries[i];
      const l = levelsSeries[i];
      points.push({
        gameNumber: i + 1,
        mixedElo: m?.newElo ?? null,
        mixedResult: m?.result ?? null,
        levelsElo: l?.newElo ?? null,
        levelsResult: l?.result ?? null,
      });
    }
    return points;
  }, [isDual, mixedSeries, levelsSeries]);

  const chartData = isDual ? combinedData : effectiveSeries;

  const { minY, maxY, ticks } = useMemo(() => {
    const values = isDual
      ? [...mixedSeries, ...levelsSeries].map((p) => p.newElo).filter((v) => v != null)
      : (effectiveSeries || []).map((p) => p.newElo).filter((v) => v != null);
    if (!values.length) return { minY: 1100, maxY: 1300, ticks: [1100, 1200, 1300] };
    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    const padding = 20;
    const min = Math.floor((rawMin - padding) / 20) * 20;
    const max = Math.ceil((rawMax + padding) / 20) * 20;
    const step = Math.max(20, Math.round((max - min) / 4 / 10) * 10);
    const t = [];
    for (let v = min; v <= max; v += step) t.push(v);
    return { minY: min, maxY: max, ticks: t };
  }, [isDual, mixedSeries, levelsSeries, effectiveSeries]);

  if (!chartData?.length) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-text-muted">
        No rated games yet for this ladder.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {isDual && (
        <div className="mb-1 flex items-center gap-3 text-xs font-medium">
          <span className="inline-flex items-center gap-1.5" style={{ color: theme.mixed }}>
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: theme.mixed }} aria-hidden />
            Mixed
          </span>
          <span className="inline-flex items-center gap-1.5" style={{ color: theme.levels }}>
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: theme.levels }} aria-hidden />
            Levels
          </span>
        </div>
      )}
      <ResponsiveContainer width="100%" height="100%" debounce={80}>
        <ComposedChart data={chartData} margin={{ top: 8, right: 10, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="4 6" vertical={false} stroke={theme.grid} strokeOpacity={0.85} />
          <XAxis
            type="number"
            dataKey="gameNumber"
            domain={[1, chartData.length]}
            allowDecimals={false}
            tick={{ fill: theme.textMuted, fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: theme.border, strokeWidth: 1 }}
          />
          <YAxis
            domain={[minY, maxY]}
            ticks={ticks}
            allowDecimals={false}
            tick={{ fill: theme.textMuted, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={36}
          />
          <Tooltip
            content={<EloTrendTooltip dual={isDual} colors={theme} />}
            cursor={{ stroke: theme.border, strokeWidth: 1, strokeDasharray: '4 4' }}
          />
          {isDual ? (
            <>
              <Line
                type="monotone"
                dataKey="mixedElo"
                stroke={theme.mixed}
                strokeWidth={2}
                connectNulls
                dot={(dotProps) => (
                  <ResultDot key={`mixed-dot-${dotProps.payload.gameNumber}`} {...dotProps} colors={theme} dataKey="mixedElo" />
                )}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="levelsElo"
                stroke={theme.levels}
                strokeWidth={2}
                connectNulls
                dot={(dotProps) => (
                  <ResultDot key={`levels-dot-${dotProps.payload.gameNumber}`} {...dotProps} colors={theme} dataKey="levelsElo" />
                )}
                isAnimationActive={false}
              />
            </>
          ) : (
            <Line
              type="monotone"
              dataKey="newElo"
              stroke={lineColor}
              strokeWidth={2}
              dot={(dotProps) => (
                <ResultDot key={`elo-dot-${dotProps.payload.gameNumber}`} {...dotProps} colors={theme} />
              )}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
